import {
  Box,
  HStack,
  Input,
  VStack,
  Text,
  Image,
  Button,
} from '@chakra-ui/react';
import { displayNotification } from '../services';
import {
  calculateChunkSize,
  calculateFileSize,
  matchIcon,
  uuid,
} from '../utils';
import { LARGE_FILE_SIZE } from '../constants';
import UploadIcon from '../assets/icons/upload.svg';
import { FileStatus } from '../models';
import { useState } from 'react';

export const NewTaskFilesUploader = ({
  files,
  setFiles,
}: {
  setFiles: React.Dispatch<
    React.SetStateAction<
      {
        file: File;
        status: FileStatus;
      }[]
    >
  >;
  files: { file: File; status: FileStatus }[];
}) => {
  const [currentFileIndex, setCurrentFileIndex] = useState<number | null>(null);
  const [currentChunkIndex, setCurrentChunkIndex] = useState<number | null>(
    null
  );

  return (
    <Box w="full">
      <Input
        display="none"
        id="files"
        onChange={e => {
          const files = (e.target as HTMLInputElement).files;
          if (!files) return;

          const transformedFiles = Array.from(files);

          const validFiles = transformedFiles.every(({ size, name }) => {
            if (size > LARGE_FILE_SIZE) {
              displayNotification({
                title: `Invalid File!`,
                description: `The file size of ${name} should be less than 5GB`,
              });
              return false;
            }
            return true;
          });

          if (validFiles) {
            setFiles(
              transformedFiles.map(file => ({
                file,
                status: FileStatus.InProgress,
              }))
            );
          }
        }}
        type="file"
        multiple
      />

      <label htmlFor="files">
        <VStack
          cursor="pointer"
          border="2px"
          borderRadius="md"
          borderStyle="dashed"
          borderColor="custom_orange.2"
          py="3"
        >
          <Box border="1px" borderColor="custom_gray.4" borderRadius="md" p="2">
            <Image src={UploadIcon} />
          </Box>

          <Text color="custom_blue.1" fontSize="sm">
            <Box as="span" color="custom_orange.2" fontWeight="semibold">
              Click to upload
            </Box>{' '}
            or drag and drop
          </Text>
          <Text color="custom_blue.1" fontSize="xs">
            Doc, PNG, JPG or GIF Files
          </Text>
        </VStack>
      </label>

      {files.map(({ file: { name, size }, status }, fileIndex) => {
        let progress = 0;
        if (status === FileStatus.Completed) {
          progress = 100;
        } else {
          const uploading = fileIndex === currentFileIndex;
          const chunks = Math.ceil(size / calculateChunkSize(size));
          if (uploading && currentChunkIndex !== null) {
            progress = Math.round((currentChunkIndex / chunks) * 100);
          } else {
            progress = 0;
          }
        }

        return (
          <Box
            key={uuid()}
            border="1px"
            borderRadius="md"
            borderColor="custom_gray.4"
            p="3"
            mt="3"
            w="full"
          >
            <HStack justify="space-between" w="full">
              <HStack gap="4" w="full">
                <Image
                  fallbackSrc="/icons/txt.svg"
                  src={matchIcon(name)}
                  alt="file icon"
                  boxSize="8"
                />

                <VStack align="stretch" w="full">
                  <Box fontWeight="medium" color="custom_blue.1" fontSize="sm">
                    {name}
                  </Box>

                  <Box color="custom_blue.1" fontSize="sm">
                    {calculateFileSize(size)} MB
                  </Box>

                  <Box color="gray.400" fontSize="xs">
                    File will be uploaded when you click on "Submit"
                  </Box>
                </VStack>
              </HStack>
            </HStack>
          </Box>
        );
      })}
    </Box>
  );
};
